import React, { useState, useEffect } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { useNavigate, useParams } from "react-router-dom";
import url from "../../config.js";
import intelogicon from "../../assets/icons/intelog_new_png.png";
import "../login/login.scss";

const domain = url.domain;

// Public invite-acceptance page. Resolves the token → shows a locked email +
// tenant name → invitee sets username + password → creates their account.
function AcceptInvite() {
  const navigate = useNavigate();
  const { token } = useParams();

  const [loadingInvite, setLoadingInvite] = useState(true);
  const [invite, setInvite] = useState(null);       // { email, tenantName, tenantRole }
  const [inviteError, setInviteError] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${domain}/api/v1/invitations/${token}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data && data.valid) setInvite(data);
        else setInviteError((data && data.message) || "This invitation is invalid or has expired.");
      })
      .catch(() => { if (!cancelled) setInviteError("Something went wrong loading your invitation."); })
      .finally(() => { if (!cancelled) setLoadingInvite(false); });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) { setError("Please choose a username."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${domain}/api/v1/invitations/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDone(true);
      } else {
        setError(data.message || "Could not create your account. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const eyeIcon = showPassword ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  return (
    <div className="login-root">
      <CssBaseline />
      <div className="login-split-container">

        {/* Left — branding */}
        <div className="login-left-side">
          <div className="branding-content">
            <div className="logo-wrapper">
              <img src={intelogicon} alt="INTELOG" className="brand-logo" />
            </div>
            <div className="quote-section">
              <h1 className="brand-headline">You're invited</h1>
              <p className="brand-description">
                {invite?.tenantName
                  ? `Set up your account to join ${invite.tenantName} on Intelog.`
                  : "Set up your account to join your team on Intelog."}
              </p>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="login-right-side">
          <div className="form-wrapper">
            {loadingInvite ? (
              <div className="form-container">
                <div className="form-header">
                  <h2 className="form-title">Loading your invitation…</h2>
                </div>
              </div>
            ) : inviteError ? (
              <div className="form-container">
                <div className="form-header">
                  <h2 className="form-title">Invitation unavailable</h2>
                  <p className="form-subtitle">{inviteError}</p>
                </div>
                <button type="button" className="form-button" onClick={() => navigate("/")}>
                  Go to Login
                </button>
              </div>
            ) : done ? (
              <div className="success-container">
                <div className="success-icon-container">
                  <div className="success-icon-circle">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                </div>
                <div className="success-message-box">
                  <h2 className="success-message-title">Account created</h2>
                  <p className="success-message-text">
                    Your account is ready. You can now sign in with your new username and password.
                  </p>
                </div>
                <button type="button" className="form-button" onClick={() => navigate("/")}>
                  Go to Login
                </button>
              </div>
            ) : (
              <div className="form-container">
                <div className="form-header">
                  <h2 className="form-title">Set up your account</h2>
                  <p className="form-subtitle">
                    {invite?.tenantName ? `Joining ${invite.tenantName}` : "Complete your Intelog account"}
                    {invite?.tenantRole === "admin" ? " as an administrator." : "."}
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <TextField
                      className="form-input"
                      variant="outlined"
                      fullWidth
                      value={invite?.email || ""}
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Username</label>
                    <TextField
                      className="form-input"
                      variant="outlined"
                      fullWidth
                      placeholder="Choose a username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <TextField
                      className="form-input"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(v => !v)} edge="end" style={{ color: '#6b849a' }}>
                              {eyeIcon}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <TextField
                      className="form-input"
                      variant="outlined"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      placeholder="Repeat your password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button type="submit" className="form-button" disabled={submitting}>
                    {submitting ? "Creating account…" : "Create Account"}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default AcceptInvite;
